import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import { cn } from '@/lib/cn';
import { useNotifications } from '@/context/NotificationsContext';
import { useLiveAlerts } from '@/context/LiveAlertsContext';
import NotificationItem from './components/NotificationItem';

type LogFilter = 'all' | 'unprocessed' | 'processed';

const FILTERS: { key: LogFilter; labelKey: string; icon: string }[] = [
  { key: 'all', labelKey: 'notifications.filter.all', icon: 'ri-list-check-2' },
  { key: 'unprocessed', labelKey: 'notifications.filter.unprocessed', icon: 'ri-time-line' },
  { key: 'processed', labelKey: 'notifications.filter.processed', icon: 'ri-checkbox-circle-line' },
];

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { notifications, loading, error, refetch, simulateIncoming, markAllProcessed } =
    useNotifications();
  const { showDemoAlert } = useLiveAlerts();
  const [filter, setFilter] = useState<LogFilter>('all');

  const counts = useMemo(
    () => ({
      all: notifications.length,
      unprocessed: notifications.filter((item) => !item.processed).length,
      processed: notifications.filter((item) => item.processed).length,
    }),
    [notifications],
  );

  const visible = useMemo(() => {
    if (filter === 'unprocessed') {
      return notifications.filter((item) => !item.processed);
    }
    if (filter === 'processed') {
      return notifications.filter((item) => item.processed);
    }
    return notifications;
  }, [notifications, filter]);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{t('notifications.title')}</h1>
          <p className="mt-1 text-sm text-foreground-500">{t('notifications.subtitle')}</p>
        </div>
        <Button variant="outline" size="md" icon="ri-refresh-line" onClick={refetch} disabled={loading}>
          {t('notifications.refresh')}
        </Button>
      </header>

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-notification-3-line text-xl leading-none" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-foreground-950">{t('notifications.listTitle')}</h2>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              icon="ri-check-double-line"
              onClick={markAllProcessed}
              disabled={loading || counts.unprocessed === 0}
            >
              {t('notifications.markAllProcessed')}
            </Button>

            <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
              {FILTERS.map((item) => {
                const active = filter === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setFilter(item.key)}
                    aria-pressed={active}
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      active
                        ? 'bg-background-50 text-foreground-950 shadow-xs'
                        : 'text-foreground-500 hover:text-foreground-800',
                    )}
                  >
                    <i className={cn(item.icon, 'text-sm leading-none')} aria-hidden="true" />
                    {t(item.labelKey)}
                    <span
                      className={cn(
                        'rounded-full px-1.5 text-[11px] font-semibold',
                        active ? 'bg-primary-100 text-primary-700' : 'bg-background-200/70 text-foreground-500',
                      )}
                    >
                      {counts[item.key]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {loading ? (
          <ul className="divide-y divide-background-100">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex gap-3.5 px-4 py-4 md:px-5">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-64 max-w-full" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="p-4 md:p-5">
            <ErrorState
              title={t('notifications.error')}
              description={t('common.errorText')}
              retryLabel={t('common.retry')}
              onRetry={refetch}
            />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 md:p-5">
            <EmptyState
              icon="ri-notification-off-line"
              title={t('notifications.empty')}
              description={t('notifications.emptyDesc')}
            />
          </div>
        ) : visible.length === 0 ? (
          <div className="p-4 md:p-5">
            <EmptyState
              icon="ri-filter-off-line"
              title={t('notifications.filter.empty')}
              description={t('notifications.filter.emptyDesc')}
            />
          </div>
        ) : (
          <ul className="divide-y divide-background-100">
            {visible.map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </ul>
        )}
      </Card>

      <Card className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex min-w-0 items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
          <span>{t('notifications.note')}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" icon="ri-flashlight-line" onClick={simulateIncoming}>
            {t('notifications.live.demo')}
          </Button>
          <Button variant="secondary" size="sm" icon="ri-alert-line" onClick={showDemoAlert}>
            {t('notifications.live.alertDemo')}
          </Button>
        </div>
      </Card>
    </div>
  );
}